<template>
  <BaseModal
    :show="show"
    @close="close"
  >
    <div class="modal-body pb-4">
      <header class="text-center pb-4 border-b border-grey">
        <h3
          class="mt-0 font-bold"
          v-text="headingText"
        />
      </header>
      <div class="mt-4">
        <label
          for="category-title"
          v-text="'Title (required)'"
        />
        <input
          id="category-title"
          v-model="category.attributes.title"
          name="category-title"
          :class="{ error: showRequiredFields && !formComplete }"
          type="text"
          class="input"
          placeholder="Title"
        >
      </div>
      <div>
        <label
          for="category-description"
          v-text="'Description'"
        />
        <textarea
          id="category-description"
          v-model="category.attributes.description"
          name="category-description"
          class="input mb-0"
          placeholder="Give some additional information about this category..."
        />
      </div>
      <div class="mt-4">
        <label
          for="category-groups"
          v-text="'Groups allowed to view category'"
        />
        <Multiselect
          id="category-groups"
          v-model="categoryGroups"
          class="category-modal__groups"
          :options="membershipGroups"
          :multiple="true"
          label="title"
          track-by="uuid"
          :close-on-select="false"
          :clear-on-select="false"
          :hide-selected="true"
          placeholder="Select Groups..."
          open-direction="bottom"
          :loading="isLoadingGroups"
          @search-change="handleSearchChange"
        />
      </div>
      <div class="flex flex-row">
        <div class="flex-1">
          <h5 v-text="'Discussions'" />
          <div class="flex flex-row items-center">
            <input
              id="post-moderation"
              v-model="category.attributes.posts_require_moderation"
              type="checkbox"
              name="post-moderation"
            >
            <label
              for="post-moderation"
              class="pt-1"
              v-text="'Require moderation'"
            />
          </div>
          <div
            v-if="category.attributes.posts_require_moderation"
            class="flex flex-col mt-1"
          >
            <div>
              <input
                id="discussion-auto-approve"
                v-model="postAutoApproval"
                type="checkbox"
                name="discussion-auto-approve"
              >
              <label
                for="discussion-auto-approve"
                class="pt-1 mb-0"
              >
                Enable auto approval
                <span
                  v-if="postAutoApproval"
                  v-text="'after'"
                />
              </label>
            </div>
            <div
              v-if="postAutoApproval"
              class="flex flex-row items-center pl-6 pt-2"
            >
              <input
                id="post-auto-approval-count"
                v-model="category.attributes.posts_auto_approval_count"
                type="number"
                name="post-auto-approval-count"
                min="2"
                style="width: 50px; padding: .25rem; margin-bottom: 0;"
                title="Auto-Approval Count"
              >
              <label
                class="mb-0 flex pl-2"
                for="post-auto-approval-count"
                v-text="'posts'"
              />
            </div>
          </div>
        </div>
        <div class="flex-1">
          <h5 v-text="'Replies'" />
          <div class="flex flex-row items-center">
            <input
              id="reply-moderation"
              v-model="category.attributes.replies_require_moderation"
              type="checkbox"
              name="reply-moderation"
            >
            <label
              for="reply-moderation"
              class="pt-1"
              v-text="'Require moderation'"
            />
          </div>
          <div
            v-if="category.attributes.replies_require_moderation"
            class="flex flex-col mt-1"
          >
            <div>
              <input
                id="posts-require-moderation"
                v-model="replyAutoApproval"
                type="checkbox"
                name="posts-require-moderation"
              >
              <label
                for="posts-require-moderation"
                class="pt-1 mb-0"
              >
                Enable auto approval
                <span
                  v-if="replyAutoApproval"
                  v-text="'after'"
                />
              </label>
            </div>
            <div
              v-if="replyAutoApproval"
              class="flex flex-row items-center pl-6 pt-2"
            >
              <input
                id="replies-auto-approval-count"
                v-model="category.attributes.replies_auto_approval_count"
                type="number"
                name="replies-auto-approval-count"
                min="2"
                style="width: 50px; padding: .25rem; margin-bottom: 0;"
                title="Auto-Approval Count"
              >
              <label
                class="mb-0 flex pl-2"
                for="replies-auto-approval-count"
                v-text="'replies'"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-footer text-center mt-4 flex flex-col sm:flex-row flex-wrap content-center items-center relative">
      <button
        v-if="exists"
        type="button"
        class="text-red pin-l md:absolute p-2"
        @click="archive"
        v-text="'Archive'"
      />
      <button
        type="button"
        class="btn"
        :class="{ 'btn--disabled': !formComplete }"
        :title="saveTitle"
        @click="save"
        v-text="saveTitle"
      />
      <button
        type="button"
        class="m-2 md:ml-6"
        @click="close"
        v-text="'Cancel'"
      />
    </div>
  </BaseModal>
</template>

<script type="text/babel">
import { mapActions, mapState } from 'vuex'
import Multiselect from 'vue-multiselect'
import { debounce } from 'lodash'

export default {
  name: 'DiscussionCategoryModal',
  components: {
    Multiselect,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      showRequiredFields: false,
      category: false,
      postAutoApproval: false,
      replyAutoApproval: false,
      categoryGroups: [],
      isLoadingGroups: false,
    }
  },
  computed: {
    ...mapState({
      membershipGroups: state => state.membership.membershipGroups.data.map(group => ({
        uuid: group.uuid,
        title: group.attributes.title,
      })),
    }),
    headingText() {
      return this.exists ? 'Edit Category' : 'Create a new discussion category'
    },
    formComplete() {
      return this.category.attributes.title.length > 0
    },
    exists() {
      return _.has(this.category, 'uuid')
    },
    saveTitle() {
      return this.exists ? 'Save' : 'Create'
    },
    selectedMembershipGroupUuids() {
      return _.map(this.categoryGroups, item => item.uuid)
    },
  },
  watch: {
    show(visible) {
      if (visible && !_.isNil(this.$store.state.discussion.modals.category.category)) {
        // This is an ugly method to prevent vuex from being reactive to changes.
        this.category = JSON.parse(
          JSON.stringify(this.$store.state.discussion.modals.category.category)
        )

        this.categoryGroups = this.category.membershipGroups.data.map(group => ({
          title: group.attributes.title,
          uuid: group.uuid,
        }))

        this.postAutoApproval = parseInt(this.category.attributes.posts_auto_approval_count) > 0

        this.replyAutoApproval = parseInt(this.category.attributes.replies_auto_approval_count) > 0

        return false
      }

      this.reset()
    },
    postAutoApproval() {
      if (this.category.attributes.posts_auto_approval_count === null) {
        this.category.attributes.posts_auto_approval_count = 2
      }
    },
    replyAutoApproval() {
      if (this.category.attributes.replies_auto_approval_count === null) {
        this.category.attributes.replies_auto_approval_count = 2
      }
    },
  },
  beforeMount() {
    this.isLoadingGroups = true

    this.fetchMembershipGroups().then(()=>{
      this.isLoadingGroups = false
    })

    this.reset()
  },
  methods: {
    ...mapActions({
      createDiscussionCategory: 'discussionCreateCategory',
      updateDiscussionCategory: 'discussionUpdateCategory',
      removeDiscussionCategory: 'discussionRemoveCategory',
      setCategoryMembershipGroups: 'discussionSetCategoryMembershipGroups',
      fetchMembershipGroups: 'membershipGetMembershipGroups',
    }),
    reset() {
      this.showRequiredFields = false
      this.postAutoApproval = false
      this.replyAutoApproval = false
      this.categoryGroups = []
      this.category = {
        attributes: {
          title: '',
          description: '',
          posts_require_moderation: false,
          replies_require_moderation: false,
          posts_auto_approval_count: 5,
          replies_auto_approval_count: 5,
        },
      }
    },
    archive() {
      this.removeDiscussionCategory(this.category.uuid).then(() => {
        this.close()
      })
    },
    save() {
      if (!this.formComplete) {
        this.showRequiredFields = true

        return false
      }

      if (this.exists) {
        Promise.all([
          // Set category membership groups
          this.setCategoryMembershipGroups({
            categoryUuid: this.category.uuid,
            params: {
              membership_group_uuids: this.selectedMembershipGroupUuids,
            },
          }).then(() => {
            // Update category
            this.updateDiscussionCategory({
              categoryUuid: this.category.uuid,
              params: Object.assign(this.category.attributes, {
                auto_approve_posts: this.postAutoApproval,
                auto_approve_replies: this.replyAutoApproval,
              }),
            })
          }),
        ]).then(() => {
          this.close()
        })
      } else {
        // Create category
        this.createDiscussionCategory(this.category.attributes).then((response) => {
          this.setCategoryMembershipGroups({
            categoryUuid: response.data.uuid,
            params: {
              membership_group_uuids: this.selectedMembershipGroupUuids,
            },
          })

          this.close()
        })
      }
    },
    handleSearchChange: debounce(function (search) {
      this.isLoadingGroups = true

      this.fetchMembershipGroups({
        params : {
          query: search,
        },
      }).then(() => {
        this.isLoadingGroups = false
      })
    }, 500),
    close() {
      this.reset()

      this.$store.commit('discussionCategoryModalClose')
    },
  },
}
</script>
